import {
    FilterProps,
    FilterValues,
    KmsTypeFilterItemBase,
    KmsTypeFilterItemCheckbox,
    SupportedKmsTypeFilter,
} from "@kaltura/mediaspace-shared-types";
import { StyledButtonLabel } from "./styled-components";
import { ChevronDown16Icon, ChevronUp16Icon } from "@kaltura/ds-react-icons";
import { Button, Menu, useMenu } from "@kaltura/ds-react-components";
import { translate } from "@kaltura/mediaspace-shared-utils";
import MultiSelectionMenuItem from "./multi-selection-menu-item/MultiSelectionMenuItem";
import SingleSelectionMenuItem from "./single-selection-menu-item/SingleSelectionMenuItem";

interface Props {
    filter: FilterProps<SupportedKmsTypeFilter>;
    filterValues: FilterValues;
    onChange: (values: FilterValues) => void;
    className?: string;
}

const Filter = (props: Props) => {
    const { filter, filterValues, onChange, className } = props;

    const { param, title } = filter;

    const { isOpen, buttonProps, menuProps } = useMenu({ menuId: `FilterMenu${param}` });

    const isChecked = (value?: string) => {
        if (!value) {
            return false;
        }
        else {
            const currentFilterValues = filterValues[filter.param];
            // filter values  can be string or array of strings
            if (Array.isArray(currentFilterValues)) {
                // can't use Array.includes because "name" and "-name" will be considered the same
                return currentFilterValues.some((val: string) => val === value);
            }
            else {
                return currentFilterValues === value;
            }
        }
    };

    const handleToggle = (value: string) => {
        const currentFilterValues = filterValues[filter.param];
        return !currentFilterValues.includes(value);
    };

    const handleFilterChange = (option: KmsTypeFilterItemCheckbox) => {
        const value = option.value;
        if (!value) {
            return;
        }
        const isItemChecked = handleToggle(value);
        const filterValuesClone = { ...filterValues };

        const currentFilterValues = filterValues[filter.param];

        if (value === "all" && currentFilterValues[0] === "all") {
            // prevent manually unchecking "all" option
            // it should only happen automatically by checking a different option
            return;
        }

        if (isItemChecked && !currentFilterValues.includes(value)) {
            // if "all" option was checked we need to remove all other values
            if (option.equalsAll) {
                filterValuesClone[filter.param] = [value];
            }
            else {
                filterValuesClone[filter.param] = [...currentFilterValues, value];
                // need to uncheck "all" option if it was checked
                if (Array.isArray(currentFilterValues)) {
                    filterValuesClone[filter.param] = (filterValuesClone[filter.param] as string[]).filter(
                        (val: string) => val !== "all"
                    );
                }
            }
        }
        else if (!isItemChecked && currentFilterValues.includes(value)) {
            if (Array.isArray(currentFilterValues)) {
                filterValuesClone[filter.param] = (currentFilterValues as string[]).filter(
                    (val: string) => val !== value
                );
            }
            else {
                if (currentFilterValues === value) {
                    filterValuesClone[filter.param] = "";
                }
            }
        }

        const checkedOptions = filterValuesClone[filter.param];
        const noOptionsChecked = checkedOptions.length === 0;
        const allOptionsChecked = checkedOptions.length === filter.items.length - 1 && !checkedOptions.includes("all");
        if (noOptionsChecked || allOptionsChecked) {
            // if no options are checked or all options are checked, check "all" option
            filterValuesClone[filter.param] = ["all"];
        }

        onChange(filterValuesClone);
    };

    const handleSingleSelectFilter = (option: KmsTypeFilterItemBase) => {
        const filterValuesClone = { ...filterValues };
        filterValuesClone[filter.param] = option.value;
        onChange(filterValuesClone);
    };

    const formatFilterLabel = () => {
        const currentFilterValues = filterValues[filter.param];
        if (Array.isArray(currentFilterValues)) {
            if (
                currentFilterValues.length > 1
                || (currentFilterValues.length === 1 && currentFilterValues[0] !== "all")
            ) {
                return `${title}: ${currentFilterValues.length}`;
            }
            else {
                return `${title}: ${translate("All")}`;
            }
        }
        else {
            // for single selection filter title is constant
            return title;
        }
    };

    return (
        <div className={className}>
            <Button
                {...buttonProps}
                variant="select"
                id={`ChannelFilterBtn${param}`}
                endIcon={isOpen ? <ChevronUp16Icon /> : <ChevronDown16Icon />}
            >
                <StyledButtonLabel>{formatFilterLabel()}</StyledButtonLabel>
            </Button>

            <Menu {...menuProps} aria-labelledby={`ChannelFilterBtn${param}`} className="kms-ds-channels-filter-menu">
                {filter.multiSelection
                    && filter.items.map((option, index) => (
                        <MultiSelectionMenuItem
                            option={option as KmsTypeFilterItemCheckbox}
                            isChecked={isChecked}
                            onChange={handleFilterChange}
                            key={"multi" + index}
                            autoFocus={index === 0}
                            disabled={option.disabled}
                        />
                    ))}

                {filter.singleSelection
                    && filter.items.map((option, index) => (
                        <SingleSelectionMenuItem
                            option={option as KmsTypeFilterItemCheckbox}
                            isChecked={isChecked}
                            onChange={handleSingleSelectFilter}
                            key={"single" + index}
                            autoFocus={index === 0}
                            disabled={option.disabled}
                        />
                    ))}
            </Menu>
        </div>
    );
};

export default Filter;
